import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export const EMOTORAD__LEAD__CREATE__FORM__ID = "f4a8a4dc-58f2-4a9f-84c9-2f890d1817d3" as Uuid;

/** Single "Create Walk-ins" action on the Walk-in event table (replaces schedule + log create entries). */
export const EMOTORAD__WALKIN__CREATE__FORM__ID = "c8e4a1b2-9d3e-4f5a-8c7e-1a2b3c4d5e6f" as Uuid;

/** Single "Create Test rides" action on the Test ride event table. */
export const EMOTORAD__TESTRIDE__CREATE__FORM__ID = "d9f5b2c3-0e4f-5a6b-9d8e-2b3c4d5e6f7a" as Uuid;

export function isEmotoradJourneyModuleConfiguration(config: JourneysModuleConfiguration): boolean {
    return config.configuration.eventContainersPage.writeConfig?.formId === EMOTORAD__LEAD__CREATE__FORM__ID;
}

export type EmotoradOption = {
    value: string;
    label: string;
};

export type EmotoradVehicleNode = {
    id: Uuid;
    name: string;
    variants: Array<{
        id: Uuid;
        name: string;
        colors: Array<{id: Uuid; name: string}>;
    }>;
};

export type EmotoradLocationNode = {
    id: Uuid;
    country: string;
    states: Array<{
        id: Uuid;
        name: string;
        cities: Array<{
            id: Uuid;
            name: string;
            pincodes: Array<{id: Uuid; code: string}>;
        }>;
    }>;
};

export type EmotoradCreateLeadTemplateProps = {
    countryCodeOptions: EmotoradOption[];
    dealerOptions: EmotoradOption[];
    exploringForOptions: EmotoradOption[];
    ridingPurposeOptions: EmotoradOption[];
    ridingFrequencyOptions: EmotoradOption[];
    riderHeightOptions: EmotoradOption[];
    otherBrandOptions: EmotoradOption[];
    sourceChannelOptions: EmotoradOption[];
    purchaseTimelineOptions: EmotoradOption[];
    vehicles: EmotoradVehicleNode[];
    locations: EmotoradLocationNode[];
};

export const EMOTORAD_CREATE_LEAD_TEMPLATE_PROPS: EmotoradCreateLeadTemplateProps = {
    countryCodeOptions: [
        {value: "+91", label: "+91"},
        {value: "+1", label: "+1"},
        {value: "+971", label: "+971"},
    ],
    dealerOptions: [
        {value: "dealer-1", label: "Emotorad Pune - FC Road"},
        {value: "dealer-2", label: "Emotorad Bengaluru - Koramangala"},
        {value: "dealer-3", label: "Emotorad Delhi - Connaught Place"},
    ],
    exploringForOptions: [
        {value: "Self", label: "Self"},
        {value: "Child", label: "Child"},
    ],
    ridingPurposeOptions: [
        {value: "commute", label: "Daily commute"},
        {value: "fitness", label: "Fitness"},
        {value: "leisure", label: "Leisure riding"},
        {value: "delivery", label: "Delivery / Work"},
    ],
    ridingFrequencyOptions: [
        {value: "daily", label: "Daily"},
        {value: "weekly-2-3", label: "2-3 times a week"},
        {value: "weekly-1", label: "Once a week"},
        {value: "rarely", label: "Rarely"},
    ],
    riderHeightOptions: [
        {value: "under-5-2", label: "Under 5'2\""},
        {value: "5-2-5-6", label: "5'2\" - 5'6\""},
        {value: "5-6-6-0", label: "5'6\" - 6'0\""},
        {value: "above-6-0", label: "Above 6'0\""},
    ],
    otherBrandOptions: [
        {value: "hero-lectro", label: "Hero Lectro"},
        {value: "ninety-one", label: "Ninety One"},
        {value: "firefox", label: "Firefox"},
        {value: "none", label: "None"},
    ],
    sourceChannelOptions: [
        {value: "website", label: "Website"},
        {value: "meta-ads", label: "Meta Ads"},
        {value: "google-ads", label: "Google Ads"},
        {value: "walk-in", label: "Walk-in"},
        {value: "referral", label: "Referral"},
    ],
    purchaseTimelineOptions: [
        {value: "Immediate", label: "Immediate"},
        {value: "1_Month", label: "Within 1 month"},
        {value: "3_Months", label: "Within 3 months"},
        {value: "Researching", label: "Researching"},
    ],
    vehicles: [
        {
            id: "caaec725-ff5b-4ea3-b267-b9f55f99b8dd" as Uuid,
            name: "T-Rex Air",
            variants: [
                {
                    id: "9bd0b858-f4cf-4cb3-9857-a53d100f0411" as Uuid,
                    name: "Single Speed",
                    colors: [
                        {id: "7f9abbd4-1f4a-4f9d-afdc-626372e4cb48" as Uuid, name: "Jet Black"},
                        {id: "5f93cd37-abfc-417c-9ef1-5ebbb9d8eb2f" as Uuid, name: "Aqua Blue"},
                    ],
                },
            ],
        },
        {
            id: "2d89772d-39f7-46d0-bcd8-e3e2dbf2998c" as Uuid,
            name: "Desire Pro",
            variants: [
                {
                    id: "1a0ba391-568d-4e7b-a851-ce8b8f3d6c21" as Uuid,
                    name: "7.65Ah",
                    colors: [
                        {id: "5a8f1b6e-0ef5-42fc-8c7a-c4647e92af0b" as Uuid, name: "Space Grey"},
                        {id: "0e56dfc7-8db8-49f6-9748-8c2ca0ec12d4" as Uuid, name: "Forest Green"},
                    ],
                },
                {
                    id: "ec9af32d-cb16-46b8-b153-6518b7db0af3" as Uuid,
                    name: "10.2Ah",
                    colors: [{id: "7cd4fd65-35e7-4c80-8fd4-20f7fa7ce2f9" as Uuid, name: "Crimson Red"}],
                },
            ],
        },
    ],
    locations: [
        {
            id: "8f58d6e8-732b-4c35-aa8f-96f10e8564c9" as Uuid,
            country: "India",
            states: [
                {
                    id: "60fd0d24-c4f0-4998-abf0-7af8ec0be9d1" as Uuid,
                    name: "Maharashtra",
                    cities: [
                        {
                            id: "78d00f9f-fd73-4e77-9b6c-9003f1f9f04d" as Uuid,
                            name: "Pune",
                            pincodes: [
                                {id: "pin-411001" as Uuid, code: "411001"},
                                {id: "pin-411004" as Uuid, code: "411004"},
                            ],
                        },
                        {
                            id: "b3c13f18-2e9d-4a4a-8d45-0f827c54a937" as Uuid,
                            name: "Mumbai",
                            pincodes: [
                                {id: "pin-400001" as Uuid, code: "400001"},
                                {id: "pin-400050" as Uuid, code: "400050"},
                            ],
                        },
                    ],
                },
                {
                    id: "8ec157f1-2ffe-4fb0-8cad-6c8a6d1b3cea" as Uuid,
                    name: "Karnataka",
                    cities: [
                        {
                            id: "e813c9a4-0d0e-4f8f-87e9-9b03d4a7b34c" as Uuid,
                            name: "Bengaluru",
                            pincodes: [
                                {id: "pin-560001" as Uuid, code: "560001"},
                                {id: "pin-560095" as Uuid, code: "560095"},
                            ],
                        },
                    ],
                },
            ],
        },
    ],
};
